import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  city = null, 
  isLoading = false,
  isBulkDelete = false,
  selectedCount = 0 
}) => {
  const [confirmationText, setConfirmationText] = useState('');

  // Reset confirmation text when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setConfirmationText('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const getModalContent = () => {
    if (isBulkDelete) {
      return {
        title: 'حذف المدن المحددة',
        description: `هل أنت متأكد من حذف ${selectedCount} مدينة محددة؟`,
        warning: 'سيتم حذف جميع البيانات المرتبطة بهذه المدن نهائياً ولن يمكن استرداد هذه البيانات.',
        confirmText: `حذف ${selectedCount} مدينة`,
        icon: 'Trash2',
        iconColor: 'text-error'
      };
    }

    return {
      title: 'حذف المدينة',
      description: `هل أنت متأكد من حذف مدينة "${city?.nameAr}"؟`,
      warning: city?.subUnionCount > 0 
        ? `تحتوي هذه المدينة على ${city?.subUnionCount} اتحاد فرعي. سيتم حذف جميع البيانات المرتبطة نهائياً.`
        : 'سيتم حذف جميع البيانات المرتبطة بهذه المدينة نهائياً ولن يمكن استرداد هذه البيانات.',
      confirmText: 'حذف المدينة',
      icon: 'AlertTriangle',
      iconColor: 'text-warning'
    };
  };

  const content = getModalContent();

  return (
    <div className="fixed inset-0 z-300 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md bg-card border border-border rounded-lg shadow-elevation-3 animate-slide-up">
          {/* Header */}
          <div className="flex items-center space-x-3 p-6 border-b border-border">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isBulkDelete ? 'bg-error/10' : 'bg-warning/10'
            }`}>
              <Icon 
                name={content?.icon} 
                size={20} 
                className={content?.iconColor}
              />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {content?.title}
              </h2>
              <p className="text-sm text-muted-foreground">
                هذا الإجراء لا يمكن التراجع عنه
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <p className="text-foreground">
              {content?.description}
            </p>
            
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={18} className="text-warning mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-warning mb-1">تحذير</p>
                  <p className="text-sm text-foreground">
                    {content?.warning}
                  </p>
                </div>
              </div>
            </div>

            {/* City Details (for single delete) */}
            {!isBulkDelete && city && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded border border-border bg-muted text-xs font-medium text-foreground">
                    {city?.colorZone || 0}
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{city?.nameAr}</p>
                    {city?.nameEn && (
                      <p className="text-sm text-muted-foreground">{city?.nameEn}</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">الاتحادات الفرعية:</span>
                    <div className="font-medium">{city?.subUnionCount}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">الحالة:</span>
                    <div className={city?.isActive ? 'text-success' : 'text-muted-foreground'}>
                      {city?.isActive ? 'نشطة' : 'غير نشطة'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Confirmation Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                اكتب "حذف" للتأكيد:
              </label>
              <input
                type="text"
                placeholder="حذف"
                value={confirmationText}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-error text-right"
                dir="rtl"
                onChange={(e) => setConfirmationText(e.target.value)}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              إلغاء
            </Button>
            
            <Button
              type="button"
              variant="destructive"
              onClick={onConfirm}
              loading={isLoading}
              disabled={isLoading || confirmationText.trim() !== 'حذف'}
              iconName="Trash2"
              iconPosition="left"
            >
              {content?.confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;